import { useState } from 'react';
import {
  AppBar,
  Badge,
  Box,
  Button,
  ButtonBase,
  Container,
  Divider,
  IconButton,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import AdminPanelSettingsRounded from '@mui/icons-material/AdminPanelSettingsRounded';
import DashboardCustomizeRounded from '@mui/icons-material/DashboardCustomizeRounded';
import ForumRounded from '@mui/icons-material/ForumRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import ManageAccountsRounded from '@mui/icons-material/ManageAccountsRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import { Link as RouterLink, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth } from '@/auth';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { FaiscaCredito } from '@/components/brand/FaiscaCredito';
import { ColorModeToggle } from '@/components/ColorModeToggle';
import { useNotify } from '@/components/feedback/notificationsContext';
import { UserAvatar } from '@/components/UserAvatar';
import { useTenant } from '@/tenant/useTenant';
import { useMarketplaceAccount } from './api';
import { specialtyPath } from './format';

const NAV = [
  { to: '/buscar', label: 'Buscar personal' },
  { to: '/para-personais', label: 'Para personais' },
];

/** Public shell of the marketplace: search engines and visitors land here. */
export function MarketplaceLayout() {
  const { pathname } = useLocation();
  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="sticky">
        <Toolbar sx={{ gap: { xs: 1, md: 3 }, minHeight: { xs: 60, sm: 68 } }}>
          <Box
            component={NavLink}
            to="/"
            aria-label="Página inicial"
            sx={{ textDecoration: 'none', color: 'inherit', minWidth: 0, mr: 'auto' }}
          >
            <BrandLogo size="small" />
          </Box>
          <Stack
            component="nav"
            direction="row"
            spacing={0.5}
            aria-label="Navegação principal"
            sx={{ display: { xs: 'none', md: 'flex' } }}
          >
            {NAV.map((item) => (
              <Button
                key={item.to}
                component={NavLink}
                to={item.to}
                color="inherit"
                sx={(theme) => ({
                  fontWeight: pathname.startsWith(item.to) ? 700 : 500,
                  color: pathname.startsWith(item.to)
                    ? theme.vars.palette.primary.main
                    : theme.vars.palette.text.primary,
                })}
              >
                {item.label}
              </Button>
            ))}
          </Stack>
          <Tooltip title="Buscar personal">
            <IconButton
              component={RouterLink}
              to="/buscar"
              aria-label="Buscar personal"
              sx={{ display: { md: 'none' } }}
            >
              <SearchRounded />
            </IconButton>
          </Tooltip>
          <ColorModeToggle />
          <AccountArea />
        </Toolbar>
      </AppBar>
      <Box component="main" sx={{ flex: 1 }}>
        <Outlet />
      </Box>
      <Footer />
    </Box>
  );
}

function AccountArea() {
  const { status, user, signOut } = useAuth();
  const account = useMarketplaceAccount();
  const navigate = useNavigate();
  const notify = useNotify();
  const { pathname, search } = useLocation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (status === 'loading') return <Box sx={{ width: 36 }} />;
  if (status === 'unauthenticated') {
    return (
      <Button
        variant="outlined"
        component={RouterLink}
        to="/login"
        state={{ from: `${pathname}${search}` }}
      >
        Entrar
      </Button>
    );
  }

  const data = account.data;
  const pending = data?.pendingReceived ?? 0;
  const go = (to: string) => {
    setAnchor(null);
    void navigate(to);
  };

  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label="Abrir menu da conta"
        aria-haspopup="menu"
        sx={{ borderRadius: '50%', ml: 0.5 }}
      >
        <Badge color="error" badgeContent={pending} overlap="circular">
          <UserAvatar user={user} sx={{ width: 36, height: 36 }} />
        </Badge>
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 260 } } }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.name ?? 'Sua conta'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {user?.email}
          </Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => go('/meus-pedidos')}>
          <ListItemIcon>
            <ForumRounded fontSize="small" />
          </ListItemIcon>
          Meus pedidos de contato
        </MenuItem>
        <MenuItem onClick={() => go('/painel')}>
          <ListItemIcon>
            <Badge color="error" variant="dot" invisible={!pending}>
              <DashboardCustomizeRounded fontSize="small" />
            </Badge>
          </ListItemIcon>
          {data?.profile
            ? `Painel do personal${pending ? ` (${pending})` : ''}`
            : 'Sou personal: criar perfil'}
        </MenuItem>
        {data?.canModerate && (
          <MenuItem onClick={() => go('/moderacao')}>
            <ListItemIcon>
              <AdminPanelSettingsRounded fontSize="small" />
            </ListItemIcon>
            Moderação
          </MenuItem>
        )}
        <MenuItem onClick={() => go('/conta')}>
          <ListItemIcon>
            <ManageAccountsRounded fontSize="small" />
          </ListItemIcon>
          Minha conta e privacidade
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={async () => {
            setAnchor(null);
            await signOut();
            notify('Você saiu da sua conta.');
          }}
        >
          <ListItemIcon>
            <LogoutRounded fontSize="small" />
          </ListItemIcon>
          Sair
        </MenuItem>
      </Menu>
    </>
  );
}

const FOOTER_GOALS = [
  ['emagrecimento', 'Emagrecimento'],
  ['hipertrofia', 'Hipertrofia'],
  ['funcional', 'Treino funcional'],
  ['terceira-idade', 'Terceira idade'],
  ['gestantes', 'Gestantes e pós-parto'],
  ['reabilitacao', 'Reabilitação'],
] as const;

function Footer() {
  const { displayName } = useTenant();
  return (
    <Box
      component="footer"
      sx={(theme) => ({
        mt: 8,
        borderTop: `1px solid ${theme.vars.palette.divider}`,
        bgcolor: theme.vars.palette.background.paper,
      })}
    >
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={4}
          sx={{ justifyContent: 'space-between' }}
        >
          <Box sx={{ maxWidth: 360 }}>
            <BrandLogo size="small" />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              O jeito simples de encontrar um personal trainer com CREF, perto de você ou online. O
              contato é direto com o profissional.
            </Typography>
          </Box>
          <Stack direction="row" spacing={{ xs: 4, sm: 6 }} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Stack spacing={1}>
              <Typography variant="overline" color="text.secondary">
                Alunos
              </Typography>
              <Link component={RouterLink} to="/buscar" color="inherit">
                Buscar personal
              </Link>
              <Link component={RouterLink} to="/personal-online" color="inherit">
                Personal online
              </Link>
              <Link component={RouterLink} to="/guias" color="inherit">
                Guias
              </Link>
              <Link component={RouterLink} to="/meus-pedidos" color="inherit">
                Meus pedidos
              </Link>
            </Stack>
            <Stack spacing={1}>
              <Typography variant="overline" color="text.secondary">
                Por objetivo
              </Typography>
              {FOOTER_GOALS.map(([key, label]) => (
                <Link key={key} component={RouterLink} to={specialtyPath(key)} color="inherit">
                  {label}
                </Link>
              ))}
            </Stack>
            <Stack spacing={1}>
              <Typography variant="overline" color="text.secondary">
                Personais
              </Typography>
              <Link component={RouterLink} to="/para-personais" color="inherit">
                Por que criar um perfil
              </Link>
              <Link component={RouterLink} to="/painel" color="inherit">
                Criar perfil grátis
              </Link>
              <Link component={RouterLink} to="/como-funciona" color="inherit">
                Como funciona
              </Link>
              <Link component={RouterLink} to="/privacidade" color="inherit">
                Privacidade
              </Link>
              <Link component={RouterLink} to="/termos" color="inherit">
                Termos de uso
              </Link>
            </Stack>
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 4 }}>
          © {new Date().getFullYear()} {displayName}. Os profissionais são independentes: valores e
          condições são combinados diretamente com eles.
        </Typography>
        <Divider sx={{ mt: 3 }} />
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
          <FaiscaCredito />
        </Box>
      </Container>
    </Box>
  );
}
